// Konec směny — „Noční kokpit": souhrn směny, dvě foto vozu s náhledem,
// tachometr s mezerami, zamčené CTA s důvodem. Validace nedokončených
// zastávek a rychlé ukončení při další trase se stejným autem beze změny.

import { useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { LAST_PATH_KEY } from "../App";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { IonContent, IonPage, IonSpinner } from "@ionic/react";
import { stopKey } from "@routera/shared";
import { api, API_BASE_URL, loadToken } from "../lib/api";
import { capturePhoto, releaseCameraStream } from "../lib/camera";
import { LamaEntry } from "../components/LamaEntry";
import { useShiftStore } from "../store/shiftStore";
import { useStopProgressStore } from "../store/stopProgressStore";
import { useGpsStore } from "../store/gpsStore";
import { ThemeToggle } from "../components/ThemeToggle";

type Step = "validate" | "finish" | "submitting";
type Shot = { blob: Blob; filename: string; preview: string };

const CameraIcon = () => (
  <svg style={{ width: 34, height: 34 }} viewBox="0 0 24 24">
    <path d="M4 8h3l1.5-2h7L17 8h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z" />
    <circle cx="12" cy="14" r="3.4" />
  </svg>
);

const fmtOdo = (raw: string) =>
  raw.replace(/[^0-9]/g, "").slice(0, 7).replace(/\B(?=(\d{3})+(?!\d))/g, " ");

export function EndShiftScreen() {
  const history = useHistory();
  const location = useLocation();
  const rid = new URLSearchParams(location.search).get("rid");
  const shift = useShiftStore((s) => s.shift);
  const clearShift = useShiftStore((s) => s.clear);
  const statusOf = useStopProgressStore((s) => s.statusOf);
  const stopGps = useGpsStore((s) => s.stop);

  const qc = useQueryClient();
  const [step, setStep] = useState<Step>("validate");
  const [error, setError] = useState<string | null>(null);
  const [odometer, setOdometer] = useState("");
  const [photoFront, setPhotoFront] = useState<Shot | null>(null);
  const [photoBack, setPhotoBack] = useState<Shot | null>(null);

  const { data: routes = [] } = useQuery({
    queryKey: ["mobile", "route", "today"],
    queryFn: api.route.today,
    staleTime: 5 * 60_000,
  });
  const route = rid ? (routes.find((r) => r.route_id === rid) ?? routes[0]) : routes[0];
  const routeLabel = route?.name ?? "trasu";

  // Další trasa se stejným autem dnes → rychlé ukončení bez fotek/km.
  const sameVehicleNext = !!(shift?.vehicle_id && routes.some(
    (r) => r.route_id !== route?.route_id
      && (r.route_status === "not_started" || !r.active_shift_id)
      && r.vehicle_id === shift.vehicle_id
  ));

  const incomplete = route
    ? route.stops
        .map((s, idx) => {
          const stopId = (s as any).stop_key ?? stopKey(s as any);
          const status = statusOf(stopId);
          return { idx, stop: s as any, stopId, status };
        })
        .filter((x) => x.status !== "done" && x.status !== "futile" && !x.stop.storno && !x.stop.collection)
    : [];

  if (!shift) {
    return (
      <IonPage>
        <IonContent scrollY={false}>
          <div className="ck scenebg" style={{ height: "100%" }}>
            <div className="pad-x"><h1>Konec směny</h1><div className="h-sub">Není aktivní směna.</div></div>
            <div className="pad-x"><LamaEntry /></div>
            <div className="bottom"><button className="cta quiet" onClick={() => history.replace("/")}>Zpět</button></div>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  const doneCount = route
    ? route.stops.filter((s: any) => {
        if (s.storno) return false;
        if (s.collection) return !!s.done;
        return statusOf(s.stop_key ?? stopKey(s)) === "done";
      }).length
    : 0;
  const futileCount = route
    ? route.stops.filter((s: any) => !s.storno && !s.collection && statusOf(s.stop_key ?? stopKey(s)) === "futile").length
    : 0;
  const activeTotal = route ? route.stops.filter((s: any) => !s.storno).length : 0;
  const shiftFrom = shift.start_ts
    ? new Date(shift.start_ts).toLocaleTimeString("cs-CZ", { hour: "2-digit", minute: "2-digit" })
    : "—";

  const handleShot = async (which: "front" | "back") => {
    setError(null);
    try {
      const photo = await capturePhoto();
      const shot: Shot = { ...photo, preview: URL.createObjectURL(photo.blob) };
      if (which === "front") setPhotoFront(shot);
      else setPhotoBack(shot);
    } catch (e: any) {
      setError(e?.message ?? "Foto se nepovedlo");
    }
  };

  const odoOk = odometer.replace(/\s/g, "").length >= 3;
  const ready = !!photoFront && !!photoBack && odoOk;
  const photoCount = (photoFront ? 1 : 0) + (photoBack ? 1 : 0);
  const whyLocked = photoCount < 2
    ? (photoCount === 0 ? "Vyfoť vůz — předek i zadek" : "Chybí druhá fotka")
    : !odoOk ? "Zadej stav tachometru" : "";

  // Po ukončení směny musí Home vidět čerstvý stav tras (route_status done) —
  // jinak by z cache (staleTime 5 min) nabízel tu samou směnu znovu.
  const invalidateRoutes = () => {
    void qc.invalidateQueries({ queryKey: ["mobile", "route", "today"] });
    void qc.invalidateQueries({ queryKey: ["mobile", "route", "status"] });
  };

  const quickEnd = async () => {
    setStep("submitting");
    try {
      await api.shift.end(shift.id, {});
      await stopGps();
      releaseCameraStream();
      clearShift();
      localStorage.removeItem(LAST_PATH_KEY);
      invalidateRoutes();
      history.replace("/");
    } catch (e: any) {
      setError(e?.message ?? "Nepodařilo se ukončit trasu");
      setStep("validate");
    }
  };

  const submit = async () => {
    setStep("submitting");
    setError(null);
    try {
      const token = await loadToken();
      if (!token) throw new Error("Token chybí");
      if (photoFront) await api.shift.uploadPhoto(API_BASE_URL, token, shift.id, "post_front", photoFront.blob, photoFront.filename);
      if (photoBack) await api.shift.uploadPhoto(API_BASE_URL, token, shift.id, "post_back", photoBack.blob, photoBack.filename);
      const odometerKm = odoOk ? parseInt(odometer.replace(/\s/g, ""), 10) : null;
      await api.shift.end(shift.id, { odometer_end: odometerKm });
      await stopGps();
      releaseCameraStream();
      clearShift();
      localStorage.removeItem(LAST_PATH_KEY);
      invalidateRoutes();
      history.replace("/");
    } catch (e: any) {
      setError(e?.message ?? "Nepodařilo se ukončit směnu");
      setStep("finish");
    }
  };

  return (
    <IonPage>
      <IonContent scrollY={false}>
        <div className="ck scenebg" style={{ height: "100%" }}>
          <div className="top">
            <button className="back" onClick={() => history.replace(route ? `/route${rid ? `?rid=${rid}` : ""}` : "/")}>‹</button>
            <span className="wm">ROUTERA <em>DRIVER</em></span>
            <ThemeToggle />
          </div>

          <div className="stopbody"><div className="pad-x">
            <h1>Konec směny</h1>
            <div className="h-sub">{routeLabel}{shiftFrom !== "—" ? ` · směna od ${shiftFrom}` : ""}</div>

            {error && (
              <div className="card" role="alert" style={{ marginTop: 12, borderColor: "rgba(242,101,88,.5)", background: "var(--bad-dim)" }}>
                <b style={{ color: "var(--bad)", fontSize: 14 }}>{error}</b>
              </div>
            )}

            {step === "validate" && (
              incomplete.length > 0 ? (
                <>
                  <div className="card" style={{ marginTop: 16, borderColor: "rgba(245,174,69,.5)", background: "var(--warn-dim)" }}>
                    <b style={{ color: "var(--warn)", fontSize: 14.5 }}>
                      {incomplete.length === 1 ? "1 zastávka bez výsledku" : `${incomplete.length} zastávky bez výsledku`}
                    </b>
                    <div style={{ fontSize: 12.5, color: "var(--soft)", marginTop: 4 }}>
                      Před koncem směny musí mít každá zastávka výsledek (dokončeno / marná jízda).
                    </div>
                  </div>
                  <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                    {incomplete.map(({ idx, stop, status }) => (
                      <button
                        key={idx}
                        className="stop"
                        onClick={() => history.push(`/route/stop/${idx}${rid ? `?rid=${rid}` : ""}`)}
                      >
                        <span className="rail">{idx + 1}</span>
                        <span className="main">
                          <span className="nm">
                            {stop.branch_id && <span className="bid">{stop.branch_id}</span>}
                            {String(stop.name ?? "Pobočka").toUpperCase()}
                          </span>
                          <span className="ad">
                            {status === "waiting" && "nezahájená"}
                            {status === "started" && "rozjetá — chybí příjezd"}
                            {status === "arrived" && "na místě — chybí výsledek"}
                          </span>
                        </span>
                        <span className="win"><span className="st-warn">vyřešit</span></span>
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <div className="sumgrid" style={{ marginTop: 16 }}>
                  <div className="sum hero"><b>{doneCount} / {activeTotal}</b><span>zastávek hotovo</span></div>
                  <div className="sum"><b>{futileCount}</b><span>marných jízd</span></div>
                  <div className="sum"><b>{shiftFrom}</b><span>začátek směny</span></div>
                </div>
              )
            )}

            {step === "finish" && (
              <div style={{ marginTop: 16 }}>
                <div className="cap" style={{ marginBottom: 8 }}>
                  Foto vozu po směně{" "}
                  <span className="mono" style={{ color: "var(--brass)", textTransform: "none", letterSpacing: 0 }}>
                    {photoCount} ze 2
                  </span>
                </div>
                <button className={`photoslot${photoFront ? " shot" : ""}`} onClick={() => void handleShot("front")}>
                  {photoFront && <img src={photoFront.preview} alt="" />}
                  <CameraIcon />
                  <span>Předek + bok{photoFront ? " ✓" : ""}</span>
                </button>
                <button className={`photoslot${photoBack ? " shot" : ""}`} onClick={() => void handleShot("back")}>
                  {photoBack && <img src={photoBack.preview} alt="" />}
                  <CameraIcon />
                  <span>Zadek + bok{photoBack ? " ✓" : ""}</span>
                </button>

                <div style={{ marginTop: 14 }}>
                  <div className="cap" style={{ marginBottom: 8 }}>Stav tachometru</div>
                  <div style={{ position: "relative" }}>
                    <input
                      className="wsfield"
                      style={{ margin: 0, textAlign: "left", letterSpacing: ".06em", paddingRight: 58 }}
                      inputMode="numeric"
                      placeholder="0"
                      value={odometer}
                      onChange={(e) => setOdometer(fmtOdo(e.target.value))}
                    />
                    <span style={{
                      position: "absolute", right: 18, top: "50%", transform: "translateY(-50%)",
                      color: "var(--dim)", fontWeight: 700, fontSize: 14,
                    }}>km</span>
                  </div>
                </div>
              </div>
            )}

            {step === "submitting" && (
              <div style={{ textAlign: "center", padding: 60 }}>
                <IonSpinner name="crescent" />
                <p style={{ color: "var(--dim)", marginTop: 12 }}>Nahrávám fotky a ukončuji směnu…</p>
              </div>
            )}
          </div></div>

          <div className="bottom">
            {step === "validate" && (
              incomplete.length > 0 ? (
                <button className="cta" onClick={() => history.replace(rid ? `/route?rid=${rid}` : "/route")}>
                  Zpět na trasu
                </button>
              ) : sameVehicleNext ? (
                <button className="cta" onClick={() => void quickEnd()}>
                  Trasa hotová — pokračovat
                </button>
              ) : (
                <button className="cta" onClick={() => setStep("finish")}>
                  Pokračovat — foto a km
                </button>
              )
            )}
            {step === "finish" && (
              <>
                <button className={`cta${!ready ? " locked" : ""}`} disabled={!ready} onClick={() => void submit()}>
                  Ukončit směnu
                </button>
                <div className="foto-why">{whyLocked}</div>
              </>
            )}
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
}
